import { Dialog } from '@material-ui/core'
import { FC, useMemo, useState } from 'react'
import { useIsMounted } from 'usehooks-ts'

import { useCompanies } from '@/hooks/use-companies'
import {
  inviteMembers,
  MemberInvitation,
  MemberInvitationResponse,
} from '@/services/invite-members'

import SubmittedContent from './submitted-content'
import UnsubmittedContent from './unsubmitted-content'

export type HandleInviteMembersRequest = (
  members: MemberInvitation[],
) => Promise<void>

type InviteModalProps = {
  handleModal: () => void
  open: boolean
}

const InviteModal: FC<InviteModalProps> = ({ handleModal, open }) => {
  const [sendingInvitations, setSendingInvitations] = useState(false)
  const [invitations, setInvitations] = useState<
    MemberInvitationResponse['users']
  >([])
  const { selectedCompany } = useCompanies()
  const controller = useMemo(() => new AbortController(), [open])
  const isMounted = useIsMounted()

  const handleModalAndAbortController = () => {
    handleModal()
    controller.abort()

    setTimeout(() => {
      isMounted() && setInvitations([])
    }, 200)
  }

  const handleInviteMembersRequest: HandleInviteMembersRequest = async (
    members,
  ) => {
    setSendingInvitations(true)
    try {
      const payload = {
        companyId: selectedCompany.id,
        companyName: selectedCompany.name,
        users: members.map((member) => ({
          ...member,
          role: 'member' as const,
        })),
      }
      const data = await inviteMembers(payload, { signal: controller.signal })
      setInvitations(data)
    } finally {
      isMounted() && setSendingInvitations(false)
    }
  }

  return (
    <Dialog open={open} onClose={handleModalAndAbortController} maxWidth='xl'>
      {invitations.length ? (
        <SubmittedContent
          handleModal={handleModalAndAbortController}
          invitations={invitations}
        />
      ) : (
        <UnsubmittedContent
          handleModal={handleModalAndAbortController}
          handleInviteMembers={handleInviteMembersRequest}
          sendingInvitations={sendingInvitations}
        />
      )}
    </Dialog>
  )
}

export default InviteModal
